import React, { useState, useMemo, useRef } from 'react';
import {
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { formatYield } from '../utils/formatters';
import type { RealYieldTimeseriesPoint } from '../types';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

interface Props {
    data?: RealYieldTimeseriesPoint[];
}

export const RealYieldTimeseriesChart: React.FC<Props> = ({ data }) => {
    const [years, setYears] = useState<number>(3);

    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    const chartData = useMemo(() => {
        if (!data) return [];
        const cutoff = new Date();
        cutoff.setFullYear(cutoff.getFullYear() - years);
        return data.filter(d => new Date(d.date) >= cutoff && d.real_yield_10y != null && d.forward_5y5y != null);
    }, [data, years]);

    if (!data || data.length === 0) return null;

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-slate-800 p-3 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg">
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 border-b border-slate-100 dark:border-slate-700 pb-2">
                        {format(parseISO(label), 'MMM d, yyyy')}
                    </p>
                    {payload.map((entry: any, index: number) => (
                        <div key={index} className="flex items-center gap-4 text-sm mt-2 justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                                <span className="font-medium text-slate-600 dark:text-slate-400">
                                    {entry.name}
                                </span>
                            </div>
                            <span className="font-bold text-slate-900 dark:text-slate-100">
                                {formatYield(entry.value)}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col h-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                        Real Yield & Forward Inflation (5Y)
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        The true cost of capital vs the Fed's ultimate inflation anchor.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                        {[1, 3, 5].map((y) => (
                            <button
                                key={y}
                                onClick={() => setYears(y)}
                                className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                                    years === y
                                        ? 'bg-white dark:bg-slate-700 shadow-sm text-indigo-600 dark:text-indigo-400'
                                        : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                }`}
                            >
                                {y}Y
                            </button>
                        ))}
                    </div>
                    <ChartDownloadMenu
                        label="Real Yield & Forward Inflation chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('real-yield_vs_forward', [`${years}Y`]) })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('real-yield_vs_forward', [`${years}Y`]) })}
                    />
                </div>
            </div>

            <div ref={containerRef} className="flex-1 w-full min-h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="ryColor" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-700" opacity={0.5} />

                        <XAxis
                            dataKey="date"
                            tickFormatter={(val) => format(parseISO(val), 'MMM yy')}
                            tick={{ fontSize: 12 }}
                            tickMargin={10}
                            minTickGap={40}
                            stroke="#94a3b8"
                        />

                        <YAxis
                            domain={['auto', 'auto']}
                            tickFormatter={(val) => `${val.toFixed(1)}%`}
                            tick={{ fontSize: 12, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                        />

                        <Tooltip content={<CustomTooltip />} />
                        <Legend verticalAlign="top" height={36} iconType="circle" />

                        <Area
                            type="monotone"
                            dataKey="real_yield_10y"
                            name="10Y Real Yield"
                            stroke="#3b82f6"
                            fill="url(#ryColor)"
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                        />

                        <Line
                            type="monotone"
                            dataKey="forward_5y5y"
                            name="5Y Forward Inflation"
                            stroke="#ef4444"
                            strokeWidth={2}
                            strokeDasharray="5 5"
                            dot={false}
                            activeDot={{ r: 6, strokeWidth: 0 }}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
